import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { children } from '../data/mockData';

const ChildrenProfiles: React.FC = () => {
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);

  const handleViewChild = (childId: string) => {
    setSelectedChildId(childId === selectedChildId ? null : childId);
  };

  // Trouver l'enfant sélectionné
  const selectedChild = children.find(child => child.id === selectedChildId);

  // Calculer le pourcentage de jours utilisés
  const calculatePercentage = (used: number, max: number) => {
    return Math.round((used / max) * 100);
  };

  return (
    <motion.div
      className="bg-white rounded-lg shadow-lg p-6"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      <h3 className="text-xl font-semibold text-gray-800 mb-4">Profils des Enfants</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {children.slice(0, 4).map((child, index) => (
          <motion.div
            key={child.id}
            className={`p-4 rounded-lg border transition-colors ${
              selectedChildId === child.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
            }`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            whileHover={{ y: -5 }}
          >
            <div className="flex justify-between items-start">
              <div className="flex items-center">
                <div className="h-10 w-10 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-bold text-lg">
                  {child.name.charAt(0)}
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-gray-900">{child.name}</h4>
                  <p className="text-xs text-gray-500">{child.age} ans</p>
                </div>
              </div>
              
              <motion.button
                className="text-sm text-blue-600 hover:text-blue-800"
                onClick={() => handleViewChild(child.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {selectedChildId === child.id ? 'Masquer' : 'Détails'}
              </motion.button>
            </div>
            
            <AnimatePresence>
              {selectedChildId === child.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4"
                >
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Jours utilisés</span>
                      <span>{child.daysUsed}/{child.maxDays}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <motion.div
                        className="bg-blue-600 h-2.5 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${calculatePercentage(child.daysUsed, child.maxDays)}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                      ></motion.div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 p-2 rounded">
                      <span className="text-gray-500">Jours restants</span>
                      <p className="text-gray-800 font-medium">{child.maxDays - child.daysUsed} jours</p>
                    </div>
                    <div className="bg-gray-50 p-2 rounded">
                      <span className="text-gray-500">Utilisation</span>
                      <p className="text-gray-800 font-medium">{calculatePercentage(child.daysUsed, child.maxDays)}%</p>
                    </div>
                  </div>
                  
                  <div className="mt-3 text-right">
                    <button className="text-xs text-blue-600 hover:text-blue-800">
                      Voir l'historique complet →
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
      
      <motion.div
        className="text-center"
        whileHover={{ scale: 1.02 }}
      >
        <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
          Voir tous les enfants ({children.length}) →
        </button>
      </motion.div>
    </motion.div>
  );
};

export default ChildrenProfiles;
